import { useEffect } from 'react';

interface PageMeta {
  title: string;
  description: string;
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  robots?: string;
}

const DEFAULT_TITLE = 'PCNGuard - Parking Ticket Control for UK Drivers and Fleets';
const DEFAULT_DESCRIPTION =
  'Scan parking tickets, review AI-powered appeal drafts, track deadlines, and control fleet PCN workflows. Android app for UK drivers and FleetMode for operators.';

export function usePageMeta({ title, description, canonical, ogTitle, ogDescription, robots = 'index,follow' }: PageMeta) {
  useEffect(() => {
    document.title = title;

    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', description);

    let canonicalEl = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (canonical) {
      if (!canonicalEl) {
        canonicalEl = document.createElement('link');
        canonicalEl.setAttribute('rel', 'canonical');
        document.head.appendChild(canonicalEl);
      }
      canonicalEl.setAttribute('href', canonical);
    }

    const ogTitleEl = document.querySelector('meta[property="og:title"]');
    if (ogTitleEl && ogTitle) ogTitleEl.setAttribute('content', ogTitle);

    const ogDescEl = document.querySelector('meta[property="og:description"]');
    if (ogDescEl && ogDescription) ogDescEl.setAttribute('content', ogDescription);

    const ogUrlEl = document.querySelector('meta[property="og:url"]');
    if (ogUrlEl && canonical) ogUrlEl.setAttribute('content', canonical);

    const twitterTitleEl = document.querySelector('meta[name="twitter:title"]');
    if (twitterTitleEl) twitterTitleEl.setAttribute('content', ogTitle || title);

    const twitterDescEl = document.querySelector('meta[name="twitter:description"]');
    if (twitterDescEl) twitterDescEl.setAttribute('content', ogDescription || description);

    let robotsEl = document.querySelector('meta[name="robots"]');
    if (!robotsEl) {
      robotsEl = document.createElement('meta');
      robotsEl.setAttribute('name', 'robots');
      document.head.appendChild(robotsEl);
    }
    robotsEl.setAttribute('content', robots);

    return () => {
      document.title = DEFAULT_TITLE;
      if (metaDesc) metaDesc.setAttribute('content', DEFAULT_DESCRIPTION);
      if (canonicalEl) canonicalEl.setAttribute('href', 'https://pcnguard.co.uk/');
      if (ogTitleEl) ogTitleEl.setAttribute('content', 'PCNGuard - Parking Ticket Control for UK Drivers and Fleets');
      if (ogDescEl)
        ogDescEl.setAttribute(
          'content',
          'Scan parking tickets, review AI-powered appeal drafts, track deadlines, and manage fleet PCN operations.'
        );
      if (ogUrlEl) ogUrlEl.setAttribute('content', 'https://pcnguard.co.uk');
      if (twitterTitleEl) twitterTitleEl.setAttribute('content', DEFAULT_TITLE);
      if (twitterDescEl)
        twitterDescEl.setAttribute(
          'content',
          'Ticket scanning, appeal drafting, deadline control, and FleetMode workflows for UK PCNs.'
        );
      if (robotsEl) robotsEl.setAttribute('content', 'index,follow');
    };
  }, [title, description, canonical, ogTitle, ogDescription, robots]);
}
